import { saveState } from './helper.js';
import debounce from 'lodash.debounce';
import logger from '@goldcast/logger';
import { createStore } from 'vuex';

import user from './modules/user';
import org from './modules/org';
import asyncActionValidation from './modules/asyncActionValidation';

import eventIntegration from '@/studio/eventIntegration/store';
import tokens from '@/studio/settings/TokensSettings/store';
import studioEventList from '@/studio/studioEventList/store/index.ts';
import studioSeriesList from '@/studio/series/studioSeriesList/store/index.ts';
import studioSeriesEventsList from '@/studio/series/studioSeriesEventsList/store/index.ts';
import studioEditEvent from '@/studio/editEvent/store/index.ts';
import studioEditSeries from '@/studio/series/editSeries/store/index.ts';
import studioEditTemplate from '@/studio/editTemplate/store/index.ts';
import studioEmailTemplate from '@/studio/emailTemplates/store/index.ts';
import studioProfessionalEducation from '@/studio/professionalEducation/store/index.ts';
import eventHubPages from '@/studio/eventHubPages/store';
import eventRecordings from '@/studio/recordings/store';
import studioRegistration from '@/studio/registration/page/store';
import studioAgenda from '@/studio/agenda/store';
import rooms from '@/studio/rooms/store';
import tags from '@/studio/tags/store';
import studioTemplateList from '@/studio/templateList/store';
import studioEmailSchedule from '@/studio/emailSchedule/store';
import studioSpeakers from '@/studio/speakers/store';
import studioStaff from '@/studio/staff/store';
import studioTemplateStaff from '@/studio/templateStaff/store';
import studioDialog from '@/studio/studioHeader/store';
import studioCheckInStaff from '@/studio/checkInStaff/store';
import studioBooths from '@/studio/booths/store';
import studioSurveys from '@/studio/surveys/store';
import seriesRegistration from '@/studio/series/registration/store';
import alerts from '@/studio/alerts/store';
import registrantsActivity from '@/studio/registrantsActivity/store/registrantsActivity.js';
import analytics from '@/studio/Analytics/store/index.ts';
import orgIntegration from '@/studio/orgIntegration/store';
import customDomain from '@/studio/settings/DomainSettings/store';
import studioToast from './modules/studioToast';
import branding from './modules/branding';
import rtmpInputs from './modules/rtmpInputs.js';
import eventList from './modules/eventList/index.js';
import contractDetails from './modules/contractDetails';
import zendeskWidget from './modules/zendeskWidget';
import contentStudio from './modules/contentStudio';
import orgPricingConfig from '@/studio/orgPricingConfig';
import recordingStudio from '@/studio/recordingStudio/store';

import registrationForm from '@/components/library/registrationForm/store';
import clientClock from './modules/clientClock.js';
import registration from './modules/registration';
import emailTemplate from '@/components/library/emailTemplate/store';
import studioPermissions from '@/studio/settings/Permissions/store';
import unauthedFeatureFlags from './modules/unauthedFeatureFlags.ts';
import workspace from './modules/workspace';
import studioForms from '@/studio/forms/store';
import studioBrandKit from '@/studio/brandKit/store';
import { getEnv } from '@/utils/env';
import router from '@/router/index.js';

const backendurl = getEnv('VUE_APP_API_BACKEND');
let responseStatusCode = null;

const store = createStore({
  actions: {
    makeapicall(
      context,
      { url, body, method, headers, noContentType, completeURL = null, useIdToken = false, unAuthTries = 0 }
    ) {
      headers = headers || {};
      noContentType = noContentType || false;
      const jwtToken = useIdToken
        ? context.state.user?.userCognitoDetails?.signInUserSession?.idToken?.jwtToken
        : context.state.user?.userCognitoDetails?.signInUserSession?.accessToken?.jwtToken;

      const payload = {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...(!!jwtToken && { Authorization: `Bearer ${jwtToken}` }),
          ...headers
        }
      };
      if (noContentType && payload?.headers && payload.headers['Content-Type']) {
        delete payload.headers['Content-Type'];
      }
      if (body) payload.body = body;
      return fetch(completeURL || `${backendurl}${url}`, payload)
        .then(res => {
          return res.status >= 500
            ? (async () => {
                let error = null;
                try {
                  error = new Error(JSON.stringify(await res.json()));
                } catch {
                  error = new Error('Internal server error');
                }
                error.name = '';
                error.statusCode = res.status;
                responseStatusCode = res.status;
                throw error;
              })()
            : res.status === 204
              ? res
              : res.json().then(data => {
                  if (res.status === 401 && unAuthTries === 0) {
                    return context.dispatch('user/getNewAccessToken', null, { root: true }).then(() => {
                      return context.dispatch('makeapicall', {
                        url,
                        body,
                        method,
                        headers,
                        noContentType,
                        unAuthTries: 1
                      });
                    });
                  } else if (res.status === 401 && unAuthTries >= 1) {
                    const error = new Error(JSON.stringify(data));
                    error.name = '';
                    error.statusCode = res.status;
                    responseStatusCode = res.status;
                    router.push({ name: 'Login' });
                    throw error;
                  } else if (res.status >= 400) {
                    const error = new Error(JSON.stringify(data));
                    error.name = '';
                    error.statusCode = res.status;
                    if (data && data['code']) {
                      error.apiErrorCode = data['code'];
                    }
                    responseStatusCode = res.status;
                    throw error;
                  } else if (res.status == 200 || res.status === 201) {
                    return data;
                  } else {
                    return res;
                  }
                });
        })
        .catch(err => {
          logger.warn('Store: makeapicall failed', { error: err });
          throw err;
        });
    }
  },
  modules: {
    tokens,
    user,
    org,
    customDomain,
    eventList,
    rtmpInputs,
    emailTemplate,
    clientClock,
    zendeskWidget,
    registrantsActivity,
    analytics,
    orgIntegration,
    eventIntegration,
    registration,
    registrationForm,
    contractDetails,
    studioToast,
    eventHubPages,
    studioEventList,
    studioSeriesList,
    studioSeriesEventsList,
    studioEditEvent,
    studioEditSeries,
    studioEditTemplate,
    eventRecordings,
    seriesRegistration,
    studioRegistration,
    studioAgenda,
    rooms,
    studioTemplateList,
    studioEmailTemplate,
    studioProfessionalEducation,
    studioEmailSchedule,
    studioSpeakers,
    studioStaff,
    studioTemplateStaff,
    studioDialog,
    studioCheckInStaff,
    studioBooths,
    studioSurveys,
    alerts,
    branding,
    tags,
    contentStudio,
    orgPricingConfig,
    studioPermissions,
    unauthedFeatureFlags,
    workspace,
    studioForms,
    asyncActionValidation,
    recordingStudio,
    studioBrandKit
  },
  strict: true
});

store.subscribe(
  debounce(() => {
    // save state in local storage
    saveState({
      currentUser: store.state.user.currentUser,
      sponsorUser: store.state.user.sponsorUser
    });
  }, 1000)
);

store.dispatch('clientClock/startClientClock');

export default store;
